const API_BASE = '/api';

let authToken: string | null = localStorage.getItem('skd_auth_token');

export function setToken(token: string | null) {
  authToken = token;
  if (token) {
    localStorage.setItem('skd_auth_token', token);
  } else {
    localStorage.removeItem('skd_auth_token');
  }
}

export function getToken(): string | null {
  return authToken;
}

export function subscribeToRecordChanges(onChange: () => void) {
  const token = getToken();
  if (!token) return () => {};

  const controller = new AbortController();

  void (async () => {
    try {
      const res = await fetch(`${API_BASE}/records/events`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      if (!res.ok || !res.body) return;

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (!controller.signal.aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const messages = buffer.split('\n\n');
        buffer = messages.pop() || '';
        for (const message of messages) {
          if (message.includes('event: records')) onChange();
        }
      }
    } catch {
      // The polling fallback handles unavailable or interrupted event streams.
    }
  })();

  return () => controller.abort();
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });
  } catch {
    throw new Error('Network error — server may be offline');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: 'Request failed' }));
    if (res.status === 401) {
      setToken(null);
    }
    throw new Error(body.error || `HTTP ${res.status}`);
  }

  return res.json();
}

export const api = {
  // Auth
  login: (pin: string) =>
    request<{ token: string; inspector: { id: string; display_name: string; is_admin: boolean } }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ pin }),
    }),

  getMe: () =>
    request<{ inspector: { id: string; display_name: string; is_admin: boolean; created_at: string } }>('/auth/me'),

  logout: () =>
    request<{ success: boolean }>('/auth/logout', { method: 'POST' }),

  register: (pin: string, display_name: string) =>
    request<{ inspector: { id: string; display_name: string } }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ pin, display_name }),
    }),

  getInspectors: () =>
    request<{ inspectors: { id: string; display_name: string; is_admin: boolean; created_at: string }[] }>('/auth/inspectors'),

  changePin: (id: string, pin: string) =>
    request<{ success: boolean }>(`/auth/inspectors/${id}/pin`, {
      method: 'PUT',
      body: JSON.stringify({ pin }),
    }),

  deleteInspector: (id: string) =>
    request<{ success: boolean }>(`/auth/inspectors/${id}`, { method: 'DELETE' }),

  toggleAdmin: (id: string) =>
    request<{ success: boolean; is_admin: boolean }>(`/auth/inspectors/${id}/admin`, { method: 'PUT' }),

  // Records
  getRecords: (search?: string) => {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    return request<{ records: any[] }>(`/records${params}`);
  },

  getRecord: (id: string) =>
    request<{ record: any }>(`/records/${id}`),

  saveRecord: (data: { metadata: any; stages: any; inspector_name?: string }) =>
    request<{ record: any }>('/records', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deleteRecord: (id: string) =>
    request<{ success: boolean }>(`/records/${id}`, { method: 'DELETE' }),

  getDefects: (id: string) =>
    request<{ defects: any[] }>(`/records/${id}/defects`),

  // Presets
  getInvoiceMappings: () =>
    request<{ mappings: { invoiceNo: string; bodies: string[] }[] }>('/presets/invoice-mappings'),

  saveInvoiceMappings: (mappings: { invoiceNo: string; bodies: string[] }[]) =>
    request<{ success: boolean }>('/presets/invoice-mappings', {
      method: 'PUT',
      body: JSON.stringify({ mappings }),
    }),

  getStandaloneChoices: () =>
    request<{ choices: { cases: string[]; boxes: string[] } }>('/presets/standalone-choices'),

  saveStandaloneChoices: (choices: { cases: string[]; boxes: string[] }) =>
    request<{ success: boolean }>('/presets/standalone-choices', {
      method: 'PUT',
      body: JSON.stringify({ choices }),
    }),
};
